import _ from 'lodash';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { checkWillReloadOnForbidden } from 'utils/forbiddenReload';
import { jsonStringify } from 'utils/json';

const FORBIDDEN_MESSAGE = 'No permissions (Forbidden).';
const FORBIDDEN_RELOAD_MESSAGE = 'No permissions (Forbidden). The page will be reloaded';

const isInvalidEntity = entity =>
  !!entity &&
  _.isObject(entity) &&
  _.isString(entity.id) &&
  _.isString(entity.entity) &&
  !!entity.errors?.every(err => _.isString(err?.propertyName) && _.isString(err?.errorMessage));

const getInvalidEntities = data => {
  const invalidEntities = data?.invalidEntities?.filter(isInvalidEntity) ?? [];

  if (!invalidEntities.length) {
    return;
  }

  return invalidEntities
    .flatMap(({ errors }) => errors)
    .reduce(
      (acc, { propertyName, errorMessage }) => ({ ...acc, [propertyName]: errorMessage }),
      {},
    );
};

// Reduce an axios error to a SAFE projection for logging.
//
// A raw axios error is NOT safe to hand to `logger`: it carries `config.data` (the serialized
// REQUEST BODY) and `config.headers` (the caller's bearer token). The logger aggregates ERROR-level
// payloads into App Insights in production, so logging the raw error on any write that carries a
// secret — e.g. the provider credential POST, whose body is
// `{"credentialKey":"...","value":"<PLAINTEXT SECRET>"}` — would write that secret to the log store
// in cleartext, defeating the point of vaulting it. Log this projection instead, never the error.
export const toLoggableError = error => ({
  status: error?.response?.status ?? null,
  message: error?.response?.data?.message ?? error?.message ?? 'Unknown error',
});

export const getDefaultErrorMessage = error => {
  const { response } = error;
  const data = response?.data;
  const multipleErrors = data?.errors || getInvalidEntities(data);

  // Services raising ThrowParameterInvalidException nest their field-level messages under
  // `error.errors` rather than the top-level `errors`; without this the generic `error.message`
  // ("Parameter is not valid") reaches the user instead of the reason the value was rejected.
  const nestedErrors = data?.error?.errors;

  const parameterErrors = data?.parameterErrors;
  const skipForbiddenError = response?.config?.skipResponseInterceptor?.forbiddenError;
  const isMultipleErrors = _.isObject(multipleErrors);
  const isDataObject = _.isPlainObject(data);

  if (isMultipleErrors) {
    return Object.values(multipleErrors).flat();
  }

  if (parameterErrors?.length) {
    return parameterErrors.map(({ message }) => message);
  }

  if (response?.status === HTTP_RESPONSE_STATUS_CODES.FORBIDDEN && !skipForbiddenError) {
    return checkWillReloadOnForbidden(error) ? FORBIDDEN_RELOAD_MESSAGE : FORBIDDEN_MESSAGE;
  }

  if (nestedErrors?.length) {
    return nestedErrors;
  }

  if (isDataObject) {
    const { error } = data;

    if (error) {
      // A refusal whose body is `{ "error": "<message>" }` carries the actionable text as a plain
      // string — reading only `error.message` dropped it and showed the generic fallback, so the
      // TPO certification approve guard's 409 told the reviewer nothing they could act on
      // (STP-549).
      const errorMessage = _.isString(error) ? error : error.message;

      return errorMessage || 'Something went wrong, check request data and try again';
    }

    return data.message || jsonStringify(data);
  }

  if (response?.status === HTTP_RESPONSE_STATUS_CODES.UNAUTHORIZED) {
    return data || 'Unauthorized';
  }

  return data || 'Unknown error';
};
